import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../primitives/form";
import { type ChoiceCardOption, ColocaChoiceCards } from "./ColocaChoiceCards";

export type { ChoiceCardOption };

type ColocaChoiceCardFieldProps = {
  /** Field name as registered in the parent `useForm` schema. Must match the Zod key exactly. */
  name: string;
  /** Visible label rendered above the card grid. */
  label?: ReactNode;
  /** Helper text rendered below the card grid. Use for hints or constraints. Do not repeat the label. */
  description?: ReactNode;
  items: ChoiceCardOption[];
  /**
   * Number of columns in the card grid. Defaults to `1`. Use `1` for long descriptions, `3` for short labels
   * with icons.
   */
  columns?: 1 | 2 | 3;
  disabled?: boolean;
};

/**
 * Controlled choice card group wired to the nearest React Hook Form context.
 *
 * Use inside a `<ColocaForm>` when options benefit from a rich card presentation — e.g. plan tiers, payment
 * methods, account types. Each card can carry an icon, label, and description. Validation errors are
 * displayed automatically via `<FormMessage>`.
 *
 * For compact lists without visual differentiation prefer `ColocaRadioGroupField`. For long or searchable
 * lists prefer `ColocaSelectField`. When the selection is component state rather than a form field, use
 * `ColocaChoiceCards` directly.
 */
export function ColocaChoiceCardField({
  name,
  label,
  description,
  items,
  columns = 1,
  disabled,
}: ColocaChoiceCardFieldProps) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem data-disabled={disabled || undefined}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <ColocaChoiceCards
              value={field.value}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              items={items}
              columns={columns}
              disabled={disabled}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
